/**
 * Tab order — the arithmetic behind drag-to-reorder, kept out of the event
 * handlers.
 *
 * THE MODEL, AND WHY IT IS THIS ONE
 * ---------------------------------
 * A drag over the strip resolves to ONE number: the index of the gap the tab
 * would land in, counted over the tabs with the dragged one removed. 0 means
 * "before everything", n means "after everything".
 *
 * The first version of this file encoded a drop as `(tab, before | after)`
 * instead, and that encoding is why the indicator flickered. It names every gap
 * twice — the space between A and B is both `(A, after)` and `(B, before)` — so
 * crossing a boundary changed the state even though the insertion point had not
 * moved, and the two spellings rendered a few pixels apart because they anchor to
 * different elements. Pointer jitter around a midpoint, or a sparse `dragover`
 * sample landing either side of it, then oscillated between two states that mean
 * the same thing. No debounce fixes an ambiguous state space; naming each gap
 * once does.
 *
 * The index is a step function of pointer x: it counts midpoints to the left, so
 * it cannot decrease while x increases. Monotonicity is a property of the
 * arithmetic rather than something the event handlers have to maintain.
 *
 * Everything here is pure and addressed by tab id. Indices are derived from the
 * geometry passed in, never remembered across events, so a list that changed
 * mid-drag cannot move the wrong tab.
 */

/** One tab's horizontal extent, in any single consistent coordinate space. */
export interface TabBounds {
  id: string;
  left: number;
  right: number;
}

/**
 * The gap the dragged tab would land in, given where the pointer is.
 *
 * Counted over the tabs EXCLUDING `draggedId`. Excluding it is what collapses the
 * two gaps either side of the dragged tab — both mean "stay put" — into a single
 * stable answer, and it is what makes the returned index directly usable as the
 * insertion point (see {@link insertTabIdAt}).
 *
 * Result is always within `[0, bounds.length - (dragged ? 1 : 0)]`.
 */
export function resolveInsertionIndex(
  bounds: readonly TabBounds[],
  draggedId: string | null,
  pointerX: number,
): number {
  let index = 0;
  for (const tab of bounds) {
    if (tab.id === draggedId) continue;
    const midpoint = tab.left + (tab.right - tab.left) / 2;
    // `>=` rather than `>`: the midpoint itself belongs to the trailing gap, so a
    // pointer resting exactly on it has one answer, not two.
    if (pointerX >= midpoint) index += 1;
    else break; // Ordered left to right — nothing further can qualify.
  }
  return index;
}

/**
 * The x coordinate to draw the insertion indicator at, in the same space as
 * `bounds`.
 *
 * Reads off the same index the drop will use, so the bar always marks the seat
 * the tab actually takes. Returns null when there is nothing to point at (no
 * tabs besides the dragged one).
 */
export function insertionIndicatorX(
  bounds: readonly TabBounds[],
  draggedId: string | null,
  index: number,
): number | null {
  const others = bounds.filter((t) => t.id !== draggedId);
  if (others.length === 0) return null;
  const clamped = Math.max(0, Math.min(index, others.length));
  // Before tab `clamped`, or past the trailing edge of the last one.
  return clamped < others.length ? others[clamped].left : others[others.length - 1].right;
}

/**
 * The id list after dropping `draggedId` into gap `index`.
 *
 * Returns the input array unchanged (same reference) when the move is a no-op or
 * the dragged id is unknown — the list moved on since the drag started, and
 * guessing a position is how the wrong tab gets moved.
 */
export function insertTabIdAt(
  ids: readonly string[],
  draggedId: string,
  index: number,
): string[] {
  const from = ids.indexOf(draggedId);
  if (from === -1) return ids as string[];

  const without = ids.filter((id) => id !== draggedId);
  const clamped = Math.max(0, Math.min(index, without.length));
  if (clamped === from) return ids as string[]; // Already in that seat.

  without.splice(clamped, 0, draggedId);
  return without;
}

/**
 * The id list after moving `tabId` one slot left or right. Backs the keyboard
 * reorder (Alt+Arrow), where there is no pointer and therefore no geometry.
 *
 * Returns the input unchanged when the tab is unknown or already at the end it is
 * being pushed towards.
 */
export function shiftTabId(
  ids: readonly string[],
  tabId: string,
  direction: -1 | 1,
): string[] {
  const from = ids.indexOf(tabId);
  if (from === -1) return ids as string[];

  const to = from + direction;
  if (to < 0 || to >= ids.length) return ids as string[];

  const next = [...ids];
  next[from] = next[to];
  next[to] = tabId;
  return next;
}

/**
 * Apply an id ordering to a list of items, defensively.
 *
 * The strip proposes an order; the state model owns the items. Ids in
 * `orderedIds` that no longer exist are ignored, and items the ordering does not
 * mention keep their relative position at the end — so a stale payload can
 * reorder wrongly at worst, and can never drop a tab (which, for a tab holding
 * unsaved edits, would be data loss).
 */
export function applyTabOrder<T>(
  items: readonly T[],
  orderedIds: readonly string[],
  getId: (item: T) => string,
): T[] {
  const byId = new Map(items.map((item) => [getId(item), item]));
  const ordered: T[] = [];

  for (const id of orderedIds) {
    const item = byId.get(id);
    if (item === undefined) continue;
    ordered.push(item);
    byId.delete(id);
  }
  // Whatever the ordering forgot, in its original relative order.
  for (const item of items) {
    if (byId.has(getId(item))) ordered.push(item);
  }

  return ordered;
}

/** True when `next` is the same sequence as `current` — lets callers skip a set(). */
export function isSameOrder(
  current: readonly string[],
  next: readonly string[],
): boolean {
  if (current.length !== next.length) return false;
  return current.every((id, index) => id === next[index]);
}

// ============================================================================
// Edge auto-scroll
// ============================================================================

/**
 * How far into each end of the strip the auto-scroll zone reaches, and how fast
 * it pulls.
 *
 * Both are interaction tuning, not thresholds papering over a bug: the insertion
 * index is stable regardless of these values. The zone is a fraction of the
 * strip's own width so it scales with the panel instead of being a pixel count
 * that feels different at 320px and 1200px, and it is capped so a very wide strip
 * does not turn most of itself into a scroll zone.
 */
const EDGE_ZONE_FRACTION = 0.15;
const EDGE_ZONE_MAX_PX = 72;
const MAX_SCROLL_PX_PER_FRAME = 14;

/**
 * Pixels to scroll this frame for a pointer at `pointerX`, negative for left.
 *
 * Zero unless the pointer is inside an edge zone AND there is something to
 * scroll to in that direction, so a drag in the middle of a non-overflowing strip
 * never scrolls. Speed ramps with depth into the zone: a pointer just inside it
 * creeps, one pinned at the edge moves at full rate, which is what makes reaching
 * a tab twenty positions away feel controllable rather than binary.
 */
export function edgeScrollDelta(
  viewport: { left: number; width: number },
  scroll: { scrollLeft: number; scrollWidth: number; clientWidth: number },
  pointerX: number,
): number {
  const maxScrollLeft = scroll.scrollWidth - scroll.clientWidth;
  if (maxScrollLeft <= 0) return 0;

  const zone = Math.min(viewport.width * EDGE_ZONE_FRACTION, EDGE_ZONE_MAX_PX);
  if (zone <= 0) return 0;

  const fromLeft = pointerX - viewport.left;
  const fromRight = viewport.left + viewport.width - pointerX;

  if (fromLeft < zone && scroll.scrollLeft > 0) {
    const depth = clamp01((zone - fromLeft) / zone);
    return -Math.min(Math.ceil(depth * MAX_SCROLL_PX_PER_FRAME), scroll.scrollLeft);
  }

  if (fromRight < zone && scroll.scrollLeft < maxScrollLeft) {
    const depth = clamp01((zone - fromRight) / zone);
    return Math.min(
      Math.ceil(depth * MAX_SCROLL_PX_PER_FRAME),
      maxScrollLeft - scroll.scrollLeft,
    );
  }

  return 0;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}
